<script>
    import { onMount } from 'svelte';
    
    // Component state
    let teachers = [];
    let loading = false;
    let error = '';
    let searchTerm = '';
    let exportLoading = false;
    
    // Filtered and sorted teachers based on search and last_login
    $: filteredTeachers = teachers
        .filter(teacher => 
            teacher.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            teacher.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            teacher.username.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            // Sort by last_login with most recent first
            // Handle null values (never logged in) - put them at the end
            if (!a.last_login && !b.last_login) return 0;
            if (!a.last_login) return 1;
            if (!b.last_login) return -1;
            
            // Compare dates (most recent first)
            return new Date(b.last_login) - new Date(a.last_login);
        });
    
    // Format last login date
    function formatLastLogin(lastLoginStr) {
        if (!lastLoginStr) return 'Niekada';
        
        const lastLogin = new Date(lastLoginStr);
        const now = new Date();
        const timeDiff = now - lastLogin;
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        
        if (daysDiff === 0) return 'Šiandien';
        if (daysDiff === 1) return 'Vakar';
        if (daysDiff < 7) return `Prieš ${daysDiff} dienas`;
        if (daysDiff < 30) return `Prieš ${Math.floor(daysDiff / 7)} savaites`;
        if (daysDiff < 365) return `Prieš ${Math.floor(daysDiff / 30)} mėnesius`;
        
        return lastLogin.toLocaleDateString('lt-LT');
    }
    
    // Get status color based on last login
    function getLoginStatus(lastLoginStr) {
        if (!lastLoginStr) return 'never-logged';
        
        const lastLogin = new Date(lastLoginStr);
        const now = new Date();
        const daysDiff = Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24));
        
        if (daysDiff <= 7) return 'recent';
        if (daysDiff <= 30) return 'moderate';
        return 'old';
    }
    
    // Fetch teachers data
    async function fetchTeachers() {
        loading = true;
        error = '';
        
        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                error = 'Reikalinga autentifikacija. Prisijunkite prie sistemos.';
                return;
            }
            
            const response = await fetch('/studentplayground/api/teachers-with-login/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                error = errorData.message || 'Nepavyko gauti mokytojų duomenų';
                return;
            }
            
            const data = await response.json();
            if (data.status === 'success') {
                teachers = data.teachers;
            } else {
                error = data.message || 'Netikėta klaida';
            }
            
        } catch (err) {
            console.error('Error fetching teachers:', err);
            error = 'Tinklo klaida. Bandykite dar kartą.';
        } finally {
            loading = false;
        }
    }
    
    // Export teachers data to CSV
    async function exportTeachersData() {
        exportLoading = true;
        
        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                error = 'Reikalinga autentifikacija. Prisijunkite prie sistemos.';
                return;
            }
            
            const response = await fetch('/studentplayground/api/teachers-export-data/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                error = errorData.message || 'Nepavyko gauti eksporto duomenų';
                return;
            }
            
            const data = await response.json();
            if (data.status === 'success') {
                downloadCSV(data.export_data);
            } else {
                error = data.message || 'Netikėta klaida';
            }
            
        } catch (err) {
            console.error('Error exporting teachers data:', err);
            error = 'Tinklo klaida. Bandykite dar kartą.';
        } finally {
            exportLoading = false;
        }
    }
    
    // Convert data to CSV and trigger download
    function downloadCSV(exportData) {
        // Define CSV headers in Lithuanian
        const headers = [
            'Mokytojo vardas',
            'Dėstomas dalykas', 
            'Dėstomi klasių intervalai',
            'El. paštas',
            'Banko sąskaitos numeris',
            'Individualios veiklos licencijos numeris',
            'Telefono numeris',
            'Darbo pradžios data',
            'Susitikimų nuoroda',
            'Laiko intervalai pateikti'
        ];
        
        // Create CSV content
        let csvContent = headers.join(',') + '\n';
        
        exportData.forEach(teacher => {
            const row = [
                `"${teacher.teacher_name}"`,
                `"${teacher.taught_subjects}"`,
                `"${teacher.taught_grades}"`, 
                `"${teacher.email}"`,
                `"${teacher.bank_account_number}"`,
                `"${teacher.individual_licence_number}"`,
                `"${teacher.phone_number}"`,
                `"${teacher.job_start_date}"`,
                `"${teacher.meeting_link}"`,
                `"${teacher.timeslots_submitted}"`
            ];
            csvContent += row.join(',') + '\n';
        });
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        const now = new Date();
        const timestamp = now.toISOString().split('T')[0]; // YYYY-MM-DD format
        const filename = `mokytojai_duomenys_${timestamp}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    // Load data when component mounts
    onMount(() => {
        fetchTeachers();
    });
</script>

<div class="teachers-list-container">
    <!-- Search and filters -->
    <div class="search-section">
        <div class="search-controls">
            <div class="search-field">
                <label for="search">Ieškoti mokytoją:</label>
                <input 
                    id="search" 
                    type="text" 
                    bind:value={searchTerm} 
                    placeholder="Vardas, pavardė, el. paštas arba naudotojo vardas..."
                />
            </div>
            <div class="export-section">
                <button 
                    class="export-button"
                    on:click={exportTeachersData}
                    disabled={exportLoading}
                >
                    {#if exportLoading}
                        <span class="loading-spinner"></span>
                        Eksportuojama...
                    {:else}
                        <span class="export-icon">📊</span>
                        Eksportuoti CSV
                    {/if}
                </button>
            </div>
        </div>
    </div>
    
    <!-- Loading state -->
    {#if loading}
        <div class="loading-message">
            <p>Kraunami duomenys...</p>
        </div>
    {/if}
    
    <!-- Error state -->
    {#if error}
        <div class="error-message">
            <p>{error}</p>
            <button on:click={fetchTeachers} class="retry-button">Bandyti dar kartą</button>
        </div>
    {/if}
    
    <!-- Teachers list -->
    {#if !loading && !error && filteredTeachers.length > 0}
        <div class="teachers-summary">
            <p>Rasta mokytojų: <strong>{filteredTeachers.length}</strong> iš {teachers.length}</p>
        </div>
        
        <div class="teachers-list">
            {#each filteredTeachers as teacher}
                <div class="teacher-card">
                    <div class="teacher-header">
                        <div class="teacher-name">
                            <h3>{teacher.first_name} {teacher.last_name}</h3>
                            <p class="teacher-email">{teacher.email}</p>
                        </div>
                        <div class="login-status {getLoginStatus(teacher.last_login)}">
                            <span class="status-indicator"></span>
                            {formatLastLogin(teacher.last_login)}
                        </div>
                    </div>
                    
                    <div class="teacher-details">
                        <div class="detail-row">
                            <span class="detail-label">Naudotojo vardas:</span>
                            <span class="detail-value">{teacher.username}</span>
                        </div>
                        
                        <div class="detail-row">
                            <span class="detail-label">Registracijos data:</span>
                            <span class="detail-value">{new Date(teacher.date_joined).toLocaleDateString('lt-LT')}</span>
                        </div>
                        
                        <div class="detail-row">
                            <span class="detail-label">Statusas:</span>
                            <span class="detail-value {teacher.is_active ? 'active' : 'inactive'}">
                                {teacher.is_active ? 'Aktyvus' : 'Neaktyvus'}
                            </span>
                        </div>
                        
                        {#if teacher.google_calendar_id}
                            <div class="detail-row">
                                <span class="detail-label">Google kalendorius:</span>
                                <span class="detail-value calendar-id">{teacher.google_calendar_id}</span>
                            </div>
                        {/if}
                        
                        {#if teacher.bank_nr && teacher.bank_nr !== '000000'}
                            <div class="detail-row">
                                <span class="detail-label">Banko nr.:</span>
                                <span class="detail-value">{teacher.bank_nr}</span>
                            </div>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    {:else if !loading && !error && filteredTeachers.length === 0 && teachers.length > 0}
        <div class="no-results">
            <p>Nerasta mokytojų pagal jūsų paieškos kriterijus.</p>
        </div>
    {:else if !loading && !error && teachers.length === 0}
        <div class="no-teachers">
            <p>Sistemoje nėra registruotų mokytojų.</p>
        </div>
    {/if}
</div>

<style>
    .teachers-list-container {
        max-width: 100%;
        margin: 0;
        padding: 0;
    }
    
    .search-section {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid #e1e5e9;
    }
    
    .search-controls {
        display: flex;
        gap: 20px;
        align-items: flex-end;
    }
    
    .search-field {
        flex: 1;
    }
    
    .search-field label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #333;
    }
    
    .search-field input {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        font-size: 16px;
        transition: border-color 0.3s ease;
    }
    
    .search-field input:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
    
    .export-section {
        display: flex;
        align-items: center;
    }
    
    .export-button {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #28a745;
        color: white;
        border: none;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        white-space: nowrap;
    }
    
    .export-button:hover:not(:disabled) {
        background: #218838;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
    
    .export-button:disabled {
        background: #6c757d;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
    
    .export-icon {
        font-size: 16px;
    }
    
    .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid transparent;
        border-top: 2px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    
    .loading-message, .error-message, .no-results, .no-teachers {
        text-align: center;
        padding: 40px 20px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    }
    
    .error-message {
        color: #dc3545;
    }
    
    .retry-button {
        background: #667eea;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 6px;
        cursor: pointer;
        margin-top: 15px;
        transition: background-color 0.3s ease;
    }
    
    .retry-button:hover {
        background: #5a67d8;
    }
    
    .teachers-summary {
        margin-bottom: 20px;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
        text-align: center;
    }
    
    .teachers-list {
        display: grid;
        gap: 20px;
    }
    
    .teacher-card {
        background: white;
        border-radius: 12px;
        padding: 25px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        border: 1px solid #e1e5e9;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    
    .teacher-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
    
    .teacher-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid #e1e5e9;
    }
    
    .teacher-name h3 {
        margin: 0 0 5px 0;
        color: #2d3748;
        font-size: 1.3em;
        font-weight: 600;
    }
    
    .teacher-email {
        margin: 0;
        color: #667eea;
        font-size: 0.95em;
    }
    
    .login-status {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 20px;
        font-size: 0.85em;
        font-weight: 500;
    }
    
    .status-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
    }
    
    .login-status.recent {
        background: #d4edda;
        color: #155724;
    }
    
    .login-status.recent .status-indicator {
        background: #28a745;
    }
    
    .login-status.moderate {
        background: #fff3cd;
        color: #856404;
    }
    
    .login-status.moderate .status-indicator {
        background: #ffc107;
    }
    
    .login-status.old {
        background: #f8d7da;
        color: #721c24;
    }
    
    .login-status.old .status-indicator {
        background: #dc3545;
    }
    
    .login-status.never-logged {
        background: #e2e3e5;
        color: #383d41;
    }
    
    .login-status.never-logged .status-indicator {
        background: #6c757d;
    }
    
    .teacher-details {
        display: grid;
        gap: 12px;
    }
    
    .detail-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
        align-items: center;
    }
    
    .detail-label {
        font-weight: 500;
        color: #4a5568;
    }
    
    .detail-value {
        color: #2d3748;
        word-break: break-word;
    }
    
    .detail-value.active {
        color: #28a745;
        font-weight: 500;
    }
    
    .detail-value.inactive {
        color: #dc3545;
        font-weight: 500;
    }
    
    .detail-value.calendar-id {
        font-family: 'Courier New', monospace;
        font-size: 0.9em;
        background: #f8f9fa;
        padding: 4px 8px;
        border-radius: 4px;
    }
    
    @media (max-width: 768px) {
        .search-controls {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
        }
        
        .export-button {
            justify-content: center;
            width: 100%;
        }
        
        .teacher-header {
            flex-direction: column;
            gap: 15px;
        }
        
        .detail-row {
            grid-template-columns: 1fr;
            gap: 5px;
        }
        
        .detail-label {
            font-size: 0.9em;
        }
    }
</style>
